/**
 * Редактор декларації з податку на прибуток (ч.1387): рядки бланка (похідні рахує сервер),
 * додаток РІ, звʼязок з фінансовою звітністю, реквізити і відмітки, «перевірити» (похідні,
 * періодичність, фінзвітність, XSD), «перерахувати з обліку», нарахування податку
 * (Дт 981 — Кт 6413 на рядок 19), друк; статус подання — з Кабінету ДПС.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  APPENDIX_TITLES, PERIOD_LABELS, fmtUah0, profitTaxDeclarations,
  type AppendixKey, type PnItem, type ProfitTaxAppendixData, type ProfitTaxCheck, type ProfitTaxFields, type VpItem,
} from '@/api/profitTaxDeclarations';
import { financialStatements, FORM_SET_TITLES, PERIOD_LABELS as FS_PERIODS } from '@/api/financialStatements';
import { contractors, orgs } from '@/api/catalogs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { DeclarationHeader, MainRows, MarksBlock, RiAppendix, SignaturesBlock } from './ProfitTaxBlank';
import { AmAppendix, PnAppendix, SimpleAppendix, VpAppendix } from './ProfitTaxAppendicesBlank';
import { formsFor } from '@/api/financialStatements';

type Tab = 'MAIN' | 'APP' | 'FS' | 'HEAD';
const APPENDIX_KEYS: AppendixKey[] = ['ri', 'am', 'zp', 'av', 'pn', 'vp'];

export default function ProfitTaxDeclarationEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [values, setValues] = useState<Record<string, string>>({});
  const [fields, setFields] = useState<ProfitTaxFields>({});
  const [ri, setRi] = useState<Record<string, string>>({});
  const [includeRi, setIncludeRi] = useState(false);
  // ч.1388: решта додатків — одним обʼєктом (am/zp/av — клітинки, pn/vp — списки, include_*).
  const [apx, setApx] = useState<ProfitTaxAppendixData>({});
  const [appTab, setAppTab] = useState<AppendixKey>('ri');
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('MAIN');
  const [check, setCheck] = useState<ProfitTaxCheck | null>(null);
  const [checking, setChecking] = useState(false);

  const docQ = useQuery({ queryKey: ['profit-tax-declaration', id], queryFn: () => profitTaxDeclarations.get(id!), enabled: !!id });
  const d = docQ.data;
  const subQ = useQuery({ queryKey: ['profit-tax-declaration-sub', id], queryFn: () => profitTaxDeclarations.submission(id!), enabled: !!id, refetchInterval: 30_000 });
  const riQ = useQuery({ queryKey: ['profit-tax-ri-rows'], queryFn: profitTaxDeclarations.riRows, staleTime: 3_600_000 });
  const rowsQ = useQuery({ queryKey: ['profit-tax-appendix-rows'], queryFn: profitTaxDeclarations.appendixRows, staleTime: 3_600_000 });
  const contractorsQ = useQuery({ queryKey: ['contractors-all'], queryFn: () => contractors.listAll(), staleTime: 300_000 });
  const nonresidents = useMemo(() => (contractorsQ.data?.items ?? []).filter((c) => c.is_non_resident), [contractorsQ.data]);
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgListQ.data?.items.find((o) => o.id === d?.organization_id);
  const fsQ = useQuery({
    queryKey: ['financial-statements-for-decl', d?.organization_id, d?.period_year, d?.period_quarter],
    queryFn: () => financialStatements.list({ organization_id: d!.organization_id, period_year: d!.period_year, period_quarter: d!.period_quarter, limit: 50 }),
    enabled: !!d,
  });
  const linkedFs = fsQ.data?.items.find((s) => s.id === d?.financial_statement_id);

  useEffect(() => {
    if (d) {
      setValues(d.values ?? {}); setFields(d.fields ?? {});
      setRi(d.appendix_data?.ri ?? {}); setIncludeRi(!!d.appendix_data?.include_ri);
      const { ri: _r, include_ri: _i, ...rest } = d.appendix_data ?? {};
      setApx(rest); setDirty(false);
    }
  }, [d]);
  const draftValue = useMemo(() => ({ values, fields, ri, includeRi, apx, dirty }), [values, fields, ri, includeRi, apx, dirty]);
  const restoreDraft = useCallback((v: typeof draftValue) => {
    setValues(v.values ?? {}); setFields(v.fields ?? {}); setRi(v.ri ?? {}); setIncludeRi(!!v.includeRi); setApx(v.apx ?? {}); setDirty(!!v.dirty);
  }, []);
  const draft = useFormDraft(draftValue, restoreDraft, { ready: !!d, clearOnAnyMutation: false });

  const subStatus = subQ.data?.status ?? null;
  const sent = !!subStatus && !['DRAFT', 'ERROR'].includes(subStatus);
  const readOnly = sent || !!d?.posted;

  const payload = () => ({ values, fields, appendix_data: { ...apx, ri, include_ri: includeRi } });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['profit-tax-declaration', id] }); qc.invalidateQueries({ queryKey: ['profit-tax-declarations'] }); };
  const save = useMutation({
    mutationFn: () => profitTaxDeclarations.update(id!, payload()),
    onSuccess: () => { setDirty(false); setError(null); setCheck(null); draft.markSaved(); invalidate(); },
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });
  const recalc = useMutation({
    mutationFn: () => profitTaxDeclarations.recalc(id!),
    onSuccess: () => { setError(null); setCheck(null); draft.markSaved(); invalidate(); },
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });
  const remove = useMutation({
    mutationFn: () => profitTaxDeclarations.remove(id!),
    onSuccess: () => { draft.markSaved(); invalidate(); navigate('/taxation/profit-tax-declarations'); },
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });
  const post = useMutation({
    mutationFn: async () => { if (dirty) await profitTaxDeclarations.update(id!, payload()); return profitTaxDeclarations.post(id!); },
    onSuccess: () => { setDirty(false); setError(null); draft.markSaved(); invalidate(); },
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });
  const unpost = useMutation({
    mutationFn: () => profitTaxDeclarations.unpost(id!),
    onSuccess: () => { setError(null); invalidate(); },
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });
  const linkFs = useMutation({
    mutationFn: (fsId: string | null) => profitTaxDeclarations.update(id!, fsId ? { financial_statement_id: fsId } : { detach_financial_statement: true }),
    onSuccess: () => { setCheck(null); invalidate(); },
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });
  const saveDocDate = useMutation({
    mutationFn: (ymd: string) => profitTaxDeclarations.update(id!, { document_date: `${ymd}T00:00:00Z` }),
    onSuccess: () => invalidate(),
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });

  async function runCheck() {
    if (!id) return;
    setChecking(true);
    try {
      if (dirty && !readOnly) await save.mutateAsync();
      setCheck(await profitTaxDeclarations.check(id));
    } catch (e: any) { setError(e?.response?.data?.detail ?? String(e)); } finally { setChecking(false); }
  }
  async function openPrint() {
    if (dirty && !readOnly) await save.mutateAsync();
    navigate(`/taxation/profit-tax-declarations/${id}/print`);
  }
  const setV = (code: string, raw: string) => {
    setValues((v) => { const n = { ...v }; if (raw === '' || Number(raw) === 0) delete n[code]; else n[code] = raw; return n; });
    setDirty(true);
  };
  const setRiV = (code: string, raw: string) => {
    setRi((v) => { const n = { ...v }; if (raw === '' || Number(raw) === 0) delete n[code]; else n[code] = raw; return n; });
    setIncludeRi(true); setDirty(true);
  };
  const setF = (patch: Partial<ProfitTaxFields>) => { setFields((f) => ({ ...f, ...patch })); setDirty(true); };
  const setA = (patch: Partial<ProfitTaxAppendixData>) => { setApx((a) => ({ ...a, ...patch })); setDirty(true); };
  const setCellOf = (key: 'am' | 'zp' | 'av') => (code: string, raw: string) => {
    setApx((a) => {
      const n = { ...(a[key] ?? {}) };
      if (raw === '' || Number(raw) === 0) delete n[code]; else n[code] = raw;
      return { ...a, [key]: n, [`include_${key}`]: true };
    });
    setDirty(true);
  };
  const included = (k: AppendixKey) => k === 'ri' ? includeRi : k === 'pn' ? (apx.pn?.length ?? 0) > 0 : k === 'vp' ? (apx.vp?.length ?? 0) > 0 : !!apx[`include_${k}` as 'include_am'];
  async function loadFiled(i: number) {
    const it = apx.vp?.[i];
    if (!it || !d) return;
    const f = await profitTaxDeclarations.filed(d.organization_id, it.year, it.quarter);
    if (!f.found) { setError(`Поданої декларації за ${PERIOD_LABELS[it.quarter]} ${it.year} у сервісі немає.`); return; }
    setA({ vp: (apx.vp ?? []).map((x, j) => (j === i ? { ...x, values: f.values, filed_number: f.document_number ?? undefined } : x)) });
  }

  if (!d) return <div className="card">Завантаження…</div>;
  const title = `Декларація з податку на прибуток — ${PERIOD_LABELS[d.period_quarter]} ${d.period_year}`;
  const tax19 = Number(values.R019G3 ?? 0);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>{title}</h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/taxation/profit-tax-declarations')}>до списку</button>
          {!readOnly && (
            <>
              <button className="primary" onClick={() => save.mutate()} disabled={save.isPending || !dirty}>зберегти</button>
              <button onClick={runCheck} disabled={checking}>{checking ? '…' : 'перевірити'}</button>
              <button disabled={recalc.isPending} onClick={() => {
                confirm('Перерахувати з обліку? Рядки 01, 02, 18 і збиток минулих років буде взято заново; клітинки РІ і відмітки лишаться.')
                  .then((ok) => { if (ok) recalc.mutate(); });
              }}>перерахувати з обліку</button>
            </>
          )}
          {readOnly && !d.posted && <button onClick={runCheck} disabled={checking}>{checking ? '…' : 'перевірити'}</button>}
          {!d.posted
            ? <button onClick={() => {
                confirm(`Нарахувати податок ${fmtUah0(tax19)} грн (Дт 981 — Кт 6413, ${PERIOD_LABELS[d.period_quarter]} ${d.period_year})?`)
                  .then((ok) => { if (ok) post.mutate(); });
              }} disabled={post.isPending} title="Проводка на рядок 19 на кінець періоду">нарахувати податок</button>
            : <button onClick={() => { confirm('Зняти нарахування податку (видалити проводку)?').then((ok) => { if (ok) unpost.mutate(); }); }}
                disabled={unpost.isPending}>розпровести</button>}
          {!readOnly && (
            <button className="danger" onClick={() => { confirm('Видалити декларацію?').then((ok) => { if (ok) remove.mutate(); }); }}>видалити</button>
          )}
          <button onClick={openPrint} title="Друкована форма декларації (і додатка РІ, якщо є)">друк</button>
        </div>
      </div>

      <div style={{ background: 'var(--primary-soft)', border: '1px solid var(--badge-blue-bd)', color: 'var(--badge-blue-fg)',
        padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <span>
          {subStatus === 'ACCEPTED' ? '✓ прийнято ДПС (квитанція №2)'
            : subStatus === 'REJECTED' ? '✗ відхилено ДПС'
            : subStatus === 'DELIVERED' ? '✓ доставлено (кв.1), очікуємо кв.2'
            : subStatus === 'SENT' ? '→ відправлено у ДПС'
            : subStatus === 'SIGNED' ? 'підписано — лишилося відправити у Кабінеті ДПС'
            : 'збережено чернеткою у Кабінеті ДПС — підпишіть і відправте там'}
          {d.posted ? ' · податок нараховано' : ''}
        </span>
        <a style={{ cursor: 'pointer', marginLeft: 'auto' }} onClick={async () => {
          if (dirty && !readOnly) await profitTaxDeclarations.update(id!, payload());
          navigate('/dps-cabinet');
        }}>Кабінет ДПС →</a>
      </div>

      <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: 8, display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <span>
          {d.document_number} · J0100130 · {fields.base_period === 'QUARTER' ? 'базовий період — квартал' : 'базовий період — рік'}
          {fields.periodicity_reason ? ` (${fields.periodicity_reason})` : ''} · суми у гривнях без копійок.
        </span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4 }} title="Дата подання (D_FILL/HFILL)">
          Дата подання
          <div style={{ width: 130 }}>
            <DatePopover value={d.document_date?.slice(0, 10) ?? ''} onChange={(v) => { if (v && !readOnly) saveDocDate.mutate(v); }} />
          </div>
        </label>
      </div>
      {error && <div style={{ color: 'var(--badge-red-fg)', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{error}</div>}

      {d.warnings.length > 0 && (
        <div style={{ background: 'var(--badge-amber-bg)', border: '1px solid var(--badge-amber-bd)', color: 'var(--badge-amber-fg)', padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.75rem' }}>
          <b>З розрахунку — перевірте:</b>
          <ul style={{ margin: '0.3rem 0 0 1.1rem', padding: 0 }}>{d.warnings.map((w, i) => <li key={i} style={{ fontSize: '0.85rem' }}>{w}</li>)}</ul>
        </div>
      )}
      {check && (
        <div style={{ marginBottom: '0.75rem' }}>
          {check.errors.length === 0 && (
            <div style={{ background: 'var(--badge-green-bg)', border: '1px solid var(--badge-green-bd)', color: 'var(--badge-green-fg)', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
              ✓ Помилок не знайдено — пакет готовий до подання: {check.package.length} файл(ів)
            </div>
          )}
          {check.errors.length > 0 && (
            <div style={{ background: 'var(--badge-red-bg)', border: '1px solid var(--badge-red-bd)', color: 'var(--badge-red-fg)', padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.5rem' }}>
              <b>Помилки ({check.errors.length}) — виправте перед поданням:</b>
              <ul style={{ margin: '0.3rem 0 0 1.1rem', padding: 0 }}>{check.errors.map((e, i) => <li key={i} style={{ fontSize: '0.85rem' }}>{e.message}</li>)}</ul>
            </div>
          )}
          {check.warnings.length > 0 && (
            <div style={{ background: 'var(--badge-amber-bg)', border: '1px solid var(--badge-amber-bd)', color: 'var(--badge-amber-fg)', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
              <b>Перевірте ({check.warnings.length}):</b>
              <ul style={{ margin: '0.3rem 0 0 1.1rem', padding: 0 }}>{check.warnings.map((w, i) => <li key={i} style={{ fontSize: '0.85rem' }}>{w.message}</li>)}</ul>
            </div>
          )}
        </div>
      )}

      <div className="seg-tabs" style={{ marginBottom: '0.6rem' }}>
        <button className={tab === 'MAIN' ? 'active' : ''} onClick={() => setTab('MAIN')}>Декларація</button>
        <button className={tab === 'APP' ? 'active' : ''} onClick={() => setTab('APP')}>
          Додатки{(() => { const n = APPENDIX_KEYS.filter(included).length; return n ? ` (${n})` : ' — не подаються'; })()}
        </button>
        <button className={tab === 'FS' ? 'active' : ''} onClick={() => setTab('FS')}>Фінансова звітність{linkedFs ? '' : ' — не привʼязана'}</button>
        <button className={tab === 'HEAD' ? 'active' : ''} onClick={() => setTab('HEAD')}>Реквізити і відмітки</button>
      </div>

      {tab === 'MAIN' && (
        <div style={{ overflowX: 'auto' }}>
          <DeclarationHeader doc={d} org={org} fields={fields} onFields={readOnly ? undefined : setF} readOnly={readOnly} />
          <MainRows doc={d} values={values} setV={setV} readOnly={readOnly} />
          <MarksBlock appendix={{ ...apx, ri, include_ri: includeRi }} fields={fields} onFields={readOnly ? undefined : setF} readOnly={readOnly}
            statementForms={linkedFs ? formsFor(linkedFs.form_set, linkedFs.period_quarter) : []}
            statementStandards={(linkedFs?.fields?.standards as 'NPSBO' | 'IFRS' | undefined) ?? null} />
          <SignaturesBlock doc={d} org={org} />
        </div>
      )}

      {tab === 'APP' && (
        <div className="seg-tabs" style={{ marginBottom: '0.6rem', flexWrap: 'wrap' }}>
          {APPENDIX_KEYS.map((k) => (
            <button key={k} className={appTab === k ? 'active' : ''} onClick={() => setAppTab(k)}
              title={included(k) ? 'подається' : 'не подається'}>
              {APPENDIX_TITLES[k]}{included(k) ? ' ✓' : ''}
            </button>
          ))}
        </div>
      )}

      {tab === 'APP' && appTab === 'am' && (
        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <input type="checkbox" checked={!!apx.include_am} disabled={readOnly} style={{ width: 'auto' }}
              onChange={(e) => setA({ include_am: e.target.checked })} />
            подавати додаток АМ (рядок 1.2.1 додатка РІ = усього за АМ)
          </label>
          {rowsQ.data ? <AmAppendix rows={rowsQ.data.am} am={apx.am ?? {}} setCell={setCellOf('am')} readOnly={readOnly} doc={d} org={org} /> : <div>Завантаження…</div>}
        </div>
      )}
      {tab === 'APP' && appTab === 'zp' && (
        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <input type="checkbox" checked={!!apx.include_zp} disabled={readOnly} style={{ width: 'auto' }}
              onChange={(e) => setA({ include_zp: e.target.checked })} />
            подавати додаток ЗП (рядок 16 ЗП декларації = рядок 16 додатка)
          </label>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: 0 }}>
            Податок, сплачений за кордоном (16.1), авансовий внесок з дивідендів цього періоду (16.2 — з додатка АВ) і не зарахований у минулі роки
            (16.3 — з додатка ЗП попередньої річної декларації), сплачені щомісячні авансові внески (16.5). Рядки 16.4, 16.4.1 (не більше податку періоду) і 16 рахує сервер.
          </p>
          {rowsQ.data ? <SimpleAppendix rows={rowsQ.data.zp} cells={apx.zp ?? {}} setCell={setCellOf('zp')} readOnly={readOnly} doc={d} org={org}
            title="Додаток ЗП до рядка 16 ЗП Податкової декларації з податку на прибуток підприємств" subtitle="Зменшення нарахованої суми податку" /> : <div>Завантаження…</div>}
        </div>
      )}
      {tab === 'APP' && appTab === 'av' && (
        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <input type="checkbox" checked={!!apx.include_av} disabled={readOnly} style={{ width: 'auto' }}
              onChange={(e) => setA({ include_av: e.target.checked })} />
            подавати додаток АВ (рядок 20 АВ декларації = рядок 6 додатка; рядок 16.2 ЗП — теж)
          </label>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: 0 }}>
            Рядок 1 заповнено з обліку (виплачені дивіденди, Дт 671). Випишіть окремо дивіденди фізособам (1.1) — з них авансовий внесок не сплачується.
            Рядок 4 — перевищення дивідендів над обʼєктом оподаткування року, за який вони виплачуються (сервіс бере попередню річну декларацію; якщо податок
            за той рік не сплачено або дивіденди за кілька років — порахуйте самі). Рядки 5 і 6 рахує сервер.
          </p>
          {rowsQ.data ? <SimpleAppendix rows={rowsQ.data.av} cells={apx.av ?? {}} setCell={setCellOf('av')} readOnly={readOnly} doc={d} org={org}
            title="Додаток АВ до рядка 20 АВ Податкової декларації з податку на прибуток підприємств"
            subtitle="Розрахунок авансового внеску з податку на прибуток підприємств на суму виплачених дивідендів (прирівняних до них платежів)" /> : <div>Завантаження…</div>}
        </div>
      )}
      {tab === 'APP' && appTab === 'pn' && rowsQ.data && (
        <div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: 0 }}>
            Окремий додаток на кожного нерезидента. Сума доходу — графа 3; ставка за Податковим кодексом підставлена, за міжнародним договором — графа 5
            (і назва договору внизу); податок, сплачений за власний рахунок при негрошовій виплаті, — графа 7. Графи 6, 8 і підсумок рахує сервер; сума всіх додатків → рядок 23 ПН.
          </p>
          <PnAppendix rows={rowsQ.data.pn} items={apx.pn ?? []} readOnly={readOnly} nonresidents={nonresidents} doc={d} org={org}
            setItems={(items: PnItem[]) => setA({ pn: items })} />
        </div>
      )}
      {tab === 'APP' && appTab === 'vp' && rowsQ.data && (
        <div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: 0 }}>
            Помилка минулого періоду виправляється у поточній декларації: оберіть період, сервіс підтягне подану декларацію як початкові значення,
            впишіть <b>правильні</b> рядки — різниця з поданою (29, 30, 33, 36), штраф 5 % і рядки 29–40 основної частини порахуються; пеню впишіть
            вручну. Інший спосіб — окрема уточнююча декларація (штраф 3 %).
          </p>
          <VpAppendix resultRows={rowsQ.data.vp} items={apx.vp ?? []} readOnly={readOnly} year={d.period_year} quarter={d.period_quarter} doc={d} org={org}
            setItems={(items: VpItem[]) => setA({ vp: items })} onLoadFiled={loadFiled} />
        </div>
      )}

      {tab === 'APP' && appTab === 'ri' && (
        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <input type="checkbox" checked={includeRi} disabled={readOnly}
              onChange={(e) => { setIncludeRi(e.target.checked); setDirty(true); }} style={{ width: 'auto' }} />
            подавати додаток РІ (рядок 03 РІ декларації = рядок 03 додатка)
          </label>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: 0 }}>
            Амортизаційні різниці (1.1.1 — бухгалтерська амортизація, 1.2.1 — податкова з додатка АМ, 1.1.2 / 1.2.2 —
            залишкова вартість вибулих обʼєктів) сервіс заповнює з обліку, коли різниці застосовуються; решту
            (резерви, фінансові операції, інші) вписуйте вручну. Збиток минулих років (рядок 3.2.4) — з попередньої
            річної декларації. Платник з доходом до 40 млн грн може не застосовувати різниці (відмітка про
            рішення на вкладці «Реквізити і відмітки») — тоді додаток подається лише зі збитком минулих років.
          </p>
          {riQ.data ? <RiAppendix rows={riQ.data} ri={ri} setRi={setRiV} readOnly={readOnly || !includeRi} autoFields={apx.ri_auto} doc={d} org={org} /> : <div>Завантаження рядків…</div>}
        </div>
      )}

      {tab === 'FS' && (
        <div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: 0 }}>
            Фінансова звітність подається разом з декларацією одним пакетом; у декларації ставляться
            відмітки про подані форми. Документ береться зі сторінки «Фінзвіт» за той самий період.
          </p>
          {linkedFs ? (
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <span>Привʼязано: <b>{linkedFs.document_number}</b> — {FORM_SET_TITLES[linkedFs.form_set]}, {FS_PERIODS[linkedFs.period_quarter]} {linkedFs.period_year}</span>
              <button onClick={() => navigate(`/reports/financial/${linkedFs.id}`)}>відкрити</button>
              {!readOnly && <button onClick={() => linkFs.mutate(null)}>відвʼязати</button>}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
              {(fsQ.data?.items.length ?? 0) > 0 ? (
                <>
                  <select defaultValue="" disabled={readOnly} onChange={(e) => { if (e.target.value) linkFs.mutate(e.target.value); }}>
                    <option value="">— оберіть документ фінзвітності за період —</option>
                    {fsQ.data!.items.map((s) => <option key={s.id} value={s.id}>{s.document_number} — {FORM_SET_TITLES[s.form_set]}</option>)}
                  </select>
                </>
              ) : (
                <span style={{ color: 'var(--badge-amber-fg)' }}>За {PERIOD_LABELS[d.period_quarter]} {d.period_year} фінансової звітності ще немає.</span>
              )}
              <button onClick={() => navigate('/reports/financial/new')}>створити фінансову звітність</button>
            </div>
          )}
        </div>
      )}

      {tab === 'HEAD' && (
        <div>
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label htmlFor="pt-kved">КВЕД</label>
              <input id="pt-kved" value={fields.kved ?? ''} disabled={readOnly} onChange={(e) => setF({ kved: e.target.value })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label htmlFor="pt-zip">Поштовий індекс</label>
              <input id="pt-zip" value={fields.zip ?? ''} disabled={readOnly} placeholder="з адреси" onChange={(e) => setF({ zip: e.target.value })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label htmlFor="pt-base">Базовий звітний період</label>
              <select id="pt-base" value={fields.base_period ?? 'YEAR'} disabled={readOnly}
                onChange={(e) => setF({ base_period: e.target.value as 'QUARTER' | 'YEAR' })}>
                <option value="QUARTER">квартал</option>
                <option value="YEAR">рік</option>
              </select>
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0.5rem 0' }}>
            <input type="checkbox" checked={!fields.apply_differences} disabled={readOnly}
              onChange={(e) => setF({ apply_differences: !e.target.checked })} style={{ width: 'auto' }} />
            прийнято рішення про незастосування коригувань на різниці (дохід до 40 млн грн; крім збитку минулих років)
          </label>
          {!fields.apply_differences && (
            <div className="field">
              <label htmlFor="pt-decision">Текст відмітки про рішення</label>
              <textarea id="pt-decision" rows={3} value={fields.decision_text ?? ''} disabled={readOnly}
                onChange={(e) => setF({ decision_text: e.target.value })} />
            </div>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0.5rem 0' }}>
            <input type="checkbox" checked={!!fields.audit_publish} disabled={readOnly}
              onChange={(e) => setF({ audit_publish: e.target.checked })} style={{ width: 'auto' }} />
            є обовʼязок оприлюднювати річну фінансову звітність разом з аудиторським звітом
          </label>
          <div className="field">
            <label htmlFor="pt-supplement">Доповнення до декларації (за потреби)</label>
            <textarea id="pt-supplement" rows={2} value={fields.supplement_text ?? ''} disabled={readOnly}
              onChange={(e) => setF({ supplement_text: e.target.value })} />
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
            Назва, податковий номер, адреса, телефон, ДПІ, керівник і головний бухгалтер — з картки організації ({org?.name ?? ''}).
          </p>
        </div>
      )}
    </div>
  );
}
